import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { SiteHeader } from "@/site/layout/SiteHeader";
import { SiteFooter } from "@/site/layout/SiteFooter";
import { SmoothScroll, getLenis } from "@/components/motion/SmoothScroll";
import { useLang } from "@/i18n/LanguageContext";
import { NeedleCursor } from "@/components/ui-kss/NeedleCursor";

/* Thin mint progress line on the right edge showing how far down the page you are. */
function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      ref.current?.style.setProperty("--scroll", max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => { window.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, []);
  return <div ref={ref} className="scroll-progress" aria-hidden="true"><i /></div>;
}

/* Small "back to top" button that appears after scrolling a long page. */
function ToTop() {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 900);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = () => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    document.getElementById("main")?.focus({ preventScroll: true });
  };
  return (
    <div className="kss-v2">
      <button type="button" onClick={go} data-testid="to-top-button" aria-label={t("toTop")} tabIndex={show ? 0 : -1} aria-hidden={!show}
        className={`to-top${show ? " on" : ""}`}>
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M7 12V2M2.5 6.5 7 2l4.5 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" /></svg>
      </button>
    </div>
  );
}

export default function Layout() {
  return (
    <SmoothScroll>
      <NeedleCursor />
      <ScrollProgress />
      <SiteHeader />
      <main id="main" tabIndex={-1}>
        <Outlet />
      </main>
      <SiteFooter />
      <ToTop />
    </SmoothScroll>
  );
}
